import React, { useEffect, useState, useCallback } from 'react';
import { Platform } from 'react-native';
import { backgroundGray, colorWhite, linkGray, colorBlack } from 'resources/styles/Colors';
import Slider from 'components/utility/Slider';
import { MainStyles } from 'resources/styles/MainStyles';
import { setAudioSettings } from 'reducers/slices/audiosettings';
import { scale } from 'react-native-size-matters';
import { TouchableOpacity } from 'components/utility/Buttons';
import { loadSoundURI, unloadSound, soundObject, pitchQuality } from 'controllers/SoundController';
import { normalize } from 'utils/misc';
import { useDispatch, useSelector } from 'react-redux';
import { PlayCircleOutlineIcon, PauseCircleOutlineIcon, StopCircleOutlineIcon, VolumeHighIcon, SpeedometerIcon } from 'app/components/utility/SVGIcons';
import { XStack } from 'tamagui';

// Thunk action creators
const updateVolumeSettings = value => dispatch => {
  dispatch(setAudioSettings({
    volume: value
  }));
  // You can add more complex logic here if needed
};
const updateSpeedSettings = value => dispatch => {
  dispatch(setAudioSettings({
    speed: value
  }));
  // You can add more complex logic here if needed
};
const DetailButtons = props => {
  // Use audio settings from the new slice
  const audioSettings = useSelector(state => state.audiosettings);
  // Fallback to old settings if needed for backward compatibility
  const oldSettings = useSelector(state => state.settings);
  const settings = audioSettings || oldSettings;
  useEffect(() => {
    loadSoundURI({
      uri: props.filename
    });
    return () => {
      unloadSound();
    };
  }, []);
  const normalizeiOS = speed => {
    return normalize(speed, 0.2, 2, 0.5, 1.5);
  };
  const normalizeAndroid = speed => {
    return normalize(speed, 0.2, 2, 0.5, 1.5);
  };
  const normalizeWeb = speed => {
    return speed;
  };
  const onPlayButton = async () => {
    let speed;
    if (Platform.OS === 'ios') {
      speed = normalizeiOS(settings.speed);
    } else if (Platform.OS === 'android') {
      speed = normalizeAndroid(settings.speed);
    } else if (Platform.OS === 'web') {
      speed = normalizeWeb(settings.speed);
    }
    if (props.playStatus !== 'paused') {
      try {
        await soundObject.setStatusAsync({
          volume: settings.volume,
          rate: speed,
          shouldCorrectPitch: true,
          pitchCorrectionQuality: pitchQuality
        });
        await soundObject.setStatusAsync({
          shouldPlay: true
        });
        props.playSound();
      } catch (e) {
        console.log(`cannot play the sound file`, e);
      }
    } else {
      try {
        await soundObject.setStatusAsync({
          volume: settings.volume,
          rate: speed,
          shouldCorrectPitch: true,
          pitchCorrectionQuality: pitchQuality
        });
        await soundObject.setStatusAsync({
          shouldPlay: true
        });
        props.playSound();
      } catch (e) {
        console.log(`cannot pause the sound file`, e);
      }
    }
  };
  const onStopButton = async () => {
    try {
      await soundObject.setStatusAsync({
        shouldPlay: false,
        positionMillis: 0
      });
      props.stopSound();
    } catch (e) {
      console.log(`cannot stop the sound file`, e);
    }
  };
  const onPauseButton = async () => {
    try {
      await soundObject.setStatusAsync({
        shouldPlay: false
      });
      props.pauseSound();
    } catch (e) {
      console.log(`cannot pause the sound file`, e);
    }
  };
  return /*#__PURE__*/React.createElement(XStack, {
    justifyContent: "space-around",
    width: "25%"
  }, props.playStatus !== 'playing' ? /*#__PURE__*/React.createElement(TouchableOpacity, {
    underlayColor: colorWhite,
    onPress: () => onPlayButton()
  }, /*#__PURE__*/React.createElement(PlayCircleOutlineIcon, {
    size: 25,
    color: colorWhite
  })) : /*#__PURE__*/React.createElement(TouchableOpacity, {
    underlayColor: colorWhite,
    onPress: () => onPauseButton()
  }, /*#__PURE__*/React.createElement(PauseCircleOutlineIcon, {
    size: 25,
    color: colorWhite
  })), /*#__PURE__*/React.createElement(TouchableOpacity, {
    underlayColor: '#fff',
    onPress: () => onStopButton()
  }, /*#__PURE__*/React.createElement(StopCircleOutlineIcon, {
    size: 25,
    color: "white"
  })));
};
const VolumeSlider = () => {
  const dispatch = useDispatch();
  // Get volume from audiosettings with fallback to old settings
  const audioSettings = useSelector(state => state.audiosettings);
  const oldSettings = useSelector(state => state.settings);
  const volume = audioSettings?.volume ?? oldSettings?.volume ?? 0.5;
  const [localVolume, setLocalVolume] = useState(volume);
  const handleSlidingComplete = useCallback(value => {
    setLocalVolume(value);
    dispatch(updateVolumeSettings(value));
  }, [dispatch]);
  return /*#__PURE__*/React.createElement(XStack, {
    justifyContent: "center",
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(VolumeHighIcon, {
    size: 25,
    color: "white"
  }), /*#__PURE__*/React.createElement(Slider, {
    style: [{
      marginLeft: 10,
      width: 80,
      height: 40
    }],
    minimumValue: 0,
    maximumValue: 1,
    step: 0.1,
    thumbTintColor: '#FFF',
    thumbStyle: {
      shadowColor: colorBlack,
      shadowOffset: {
        width: 0,
        height: 0
      },
      shadowOpacity: 1,
      shadowRadius: scale(0.75),
      elevation: 7
    },
    minimumTrackTintColor: backgroundGray,
    maximumTrackTintColor: linkGray,
    value: localVolume,
    onSlidingComplete: handleSlidingComplete
  }));
};
const SpeedSlider = () => {
  const dispatch = useDispatch();
  // Get speed from audiosettings with fallback to old settings
  const audioSettings = useSelector(state => state.audiosettings);
  const oldSettings = useSelector(state => state.settings);
  const defaultSpeed = 1.0;
  const speed = audioSettings?.speed ?? oldSettings?.speed ?? defaultSpeed;
  const [localSpeed, setLocalSpeed] = useState(speed);
  const handleSlidingComplete = useCallback(value => {
    setLocalSpeed(value);
    dispatch(updateSpeedSettings(value));
  }, [dispatch]);
  return /*#__PURE__*/React.createElement(XStack, {
    justifyContent: "space-between",
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(SpeedometerIcon, {
    size: 25,
    color: colorWhite
  }), /*#__PURE__*/React.createElement(Slider, {
    style: [Platform.OS === 'web' ? MainStyles.sliderSizeWeb : MainStyles.sliderSize, {
      marginLeft: 10,
      width: 80,
      height: 40
    }],
    minimumValue: 0.2,
    maximumValue: 2,
    step: 0.1,
    thumbTintColor: '#FFF',
    thumbStyle: {
      shadowColor: colorBlack,
      shadowOffset: {
        width: 0,
        height: 0
      },
      shadowOpacity: 1,
      shadowRadius: scale(0.75),
      elevation: 7
    },
    minimumTrackTintColor: backgroundGray,
    maximumTrackTintColor: linkGray,
    value: localSpeed,
    onSlidingComplete: handleSlidingComplete
  }));
};
const Controls = props => {
  return /*#__PURE__*/React.createElement(XStack, {
    width: "100%",
    alignItems: "center",
    justifyContent: "space-around",
    pr: 2
  }, /*#__PURE__*/React.createElement(DetailButtons, props), /*#__PURE__*/React.createElement(XStack, {
    justifyContent: "space-between",
    space: 5
  }, /*#__PURE__*/React.createElement(SpeedSlider, null), /*#__PURE__*/React.createElement(VolumeSlider, null)));
};
export default Controls;